/**
 * The route registry (PRD §2 / §8 — "real routing/URL/history"). Route IDs are taken
 * verbatim from `prototype/flows.md`'s route map and must not change — they are the map's
 * primary key, and P7/P9/P15 register their screen components against these IDs.
 *
 * This module owns matching and URL shape; it renders nothing and decides no per-route
 * policy (see `guards.ts`).
 */
import type { GateId } from '@selogate/core'

export type RouteId =
  | 'home'
  | 'gate'
  | 'personalize'
  | 'people'
  // `access` is the **third** id with no `flows.md` row (D265): the panel it renders was a
  // `<section>` inside `gate` when the prototype was written, so there was never a route there to
  // take verbatim. It is a gate route rather than a shell surface — it carries `gateId` and is
  // reached from Gate Detail's one `ניהול גישה ›` row and from nowhere else, no drawer entry.
  | 'access'
  | 'invite'
  | 'schedule'
  | 'activity'
  | 'activityRow'
  | 'device'
  | 'copyMembers'
  | 'devices'
  | 'shabbat'
  | 'install'
  // `installEdit` is the **fourth** id with no `flows.md` row (D267), and it is a second door
  // onto **D176's** update branch rather than a screen of its own: `InstallScreen` renders it in
  // `mode: 'update'`, seeded straight to `updateMenu`. It is separate from `install` rather than a
  // param on it because `install` is `/install` — a path with no segment to carry a gate — and
  // because the two entries differ in what is behind them: nothing here, and an unreturnable scan
  // there, which is what makes this one's exits `navigate.back()`.
  | 'installEdit'
  | 'auth'
  // `about` is the one ID with no `flows.md` row: the prototype has no About screen, so there
  // is nothing there for it to be taken verbatim from. It is a shell surface (the settings
  // drawer's About row), not a gate route, which is why it carries no params and no guard of
  // its own beyond the shell's `requireAuth`.
  | 'about'
  // `preferences` is the second (D188), and a shell surface for the same reasons: it is the
  // destination of a sidebar row, it carries no gate and no params, and the prototype has no
  // screen for it either — the appearance preference did not exist when `flows.md` was written.
  | 'preferences'

// `Record<never, never>`, not `Record<string, never>` — the latter carries a real index
// signature (`[key: string]: never`) that then conflicts with the `id` property every
// `RouteOf<K>` intersects it with. `Record<never, never>` is the empty type with no index
// signature at all, which is what "no extra params" actually means here.
export interface RouteParamsById {
  readonly home: Record<never, never>
  readonly gate: { readonly gateId: GateId }
  readonly personalize: { readonly gateId: GateId }
  readonly people: { readonly gateId: GateId }
  readonly access: { readonly gateId: GateId }
  readonly invite: { readonly gateId: GateId }
  readonly schedule: { readonly gateId: GateId }
  readonly activity: { readonly gateId: GateId }
  readonly activityRow: { readonly gateId: GateId; readonly activityId: string }
  readonly device: { readonly gateId: GateId }
  readonly copyMembers: { readonly gateId: GateId; readonly direction: 'from' | 'to' }
  readonly devices: Record<never, never>
  readonly shabbat: { readonly gateId: GateId }
  readonly install: Record<never, never>
  readonly installEdit: { readonly gateId: GateId }
  readonly auth: Record<never, never>
  readonly about: Record<never, never>
  readonly preferences: Record<never, never>
}

export type Route = {
  readonly [K in RouteId]: { readonly id: K } & RouteParamsById[K]
}[RouteId]

export type RouteOf<K extends RouteId> = { readonly id: K } & RouteParamsById[K]

export const HOME_ROUTE: RouteOf<'home'> = { id: 'home' }
export const AUTH_ROUTE: RouteOf<'auth'> = { id: 'auth' }

/** `:param` path templates. Order matters only for readability; matching is per-route. */
const PATH_TEMPLATES: Readonly<Record<RouteId, string>> = {
  home: '/',
  gate: '/gates/:gateId',
  personalize: '/gates/:gateId/personalize',
  people: '/gates/:gateId/people',
  access: '/gates/:gateId/access',
  invite: '/gates/:gateId/invite',
  schedule: '/gates/:gateId/schedule',
  activity: '/gates/:gateId/activity',
  activityRow: '/gates/:gateId/activity/:activityId',
  device: '/gates/:gateId/device',
  copyMembers: '/gates/:gateId/people/copy/:direction',
  devices: '/devices',
  shabbat: '/gates/:gateId/shabbat',
  install: '/install',
  installEdit: '/gates/:gateId/installation',
  auth: '/auth',
  about: '/about',
  preferences: '/preferences',
}

interface CompiledPattern {
  readonly id: RouteId
  readonly keys: readonly string[]
  readonly regExp: RegExp
  readonly template: string
}

function compile(id: RouteId, template: string): CompiledPattern {
  const keys: string[] = []
  const source = template
    .split('/')
    .map((segment) => {
      if (segment.startsWith(':')) {
        keys.push(segment.slice(1))
        return '([^/]+)'
      }
      return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    })
    .join('/')
  return { id, keys, regExp: new RegExp(`^${source}$`), template }
}

const COMPILED: readonly CompiledPattern[] = (Object.keys(PATH_TEMPLATES) as RouteId[]).map((id) =>
  compile(id, PATH_TEMPLATES[id]),
)

/**
 * Every `RouteId`, derived from `PATH_TEMPLATES` rather than a second hand-typed list —
 * `infra/tests/route-coverage.test.ts`'s own lesson (`AGENTS.md` §4.5: "a guard that passed
 * vacuously twice"). R1-10 (`R1-client-resilience.md`) is what needs this: its placeholder test
 * enumerates every id and fails if any one's rendered app-bar title equals the raw id, so the
 * check stays true of an id nobody has hand-added it for yet.
 */
export const ROUTE_IDS: readonly RouteId[] = Object.keys(PATH_TEMPLATES) as RouteId[]

/** Builds the URL path for a route. `encodeURIComponent`s every param value. */
export function buildPath(route: Route): string {
  const template = PATH_TEMPLATES[route.id]
  const params = route as unknown as Record<string, string>
  return template.replace(/:([a-zA-Z]+)/g, (_match, key: string) => {
    const value = params[key]
    if (value === undefined) {
      throw new Error(`Route "${route.id}" is missing required param "${key}"`)
    }
    return encodeURIComponent(value)
  })
}

/** Matches a URL pathname against the registry. Returns `undefined` on no match (→ 404/Home). */
export function matchPath(pathname: string): Route | undefined {
  for (const pattern of COMPILED) {
    const found = pathname.match(pattern.regExp)
    if (found === null) continue
    const params: Record<string, string> = {}
    pattern.keys.forEach((key, index) => {
      params[key] = decodeURIComponent(found[index + 1] ?? '')
    })
    return { id: pattern.id, ...params } as Route
  }
  return undefined
}
